import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import {
  Button,
  TextField,
  Modal,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import { useAuth } from "../AuthContext";
import loadingGif from '../loading.gif';
import DeleteIcon from '@mui/icons-material/Delete';

const MyCV = () => {
  const { token, uid } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [photo, setPhoto] = useState(null);
  const { register, control, handleSubmit, reset } = useForm();

  const { fields: languageFields, append: appendLanguage, remove: removeLanguage } = useFieldArray({
    control,
    name: "languages",
  });

  const { fields: linkFields, append: appendLink, remove: removeLink } = useFieldArray({
    control,
    name: "links",
  });

  const { fields: skillFields, append: appendSkill, remove: removeSkill } = useFieldArray({
    control,
    name: "skills",
  });

  const [isVisible, setIsVisible] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `https://sirius.runasp.net/api/team/UserGet`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ UserId: uid }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          setUserData(data);
          setIsVisible(data.visible ? "true" : "false"); // Set visibility based on the fetched data
          reset(data); // Initialize form with fetched data
        } else {
          console.error("Failed to fetch data");
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, uid, reset]);

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleClose = () => {
    setEditMode(false);
  };

  const onSubmit = async (formData) => {
    const updateFormData = new FormData();

    updateFormData.append("Name", formData.userName);
    updateFormData.append("UserId", uid);
    updateFormData.append("Summary", formData.summary);
    updateFormData.append("Position", formData.position);
    updateFormData.append("visible", isVisible); // Add visibility to the form data

    if (formData.contacts?.phoneNumber) {
      updateFormData.append("Contacts[PhoneNumber]", formData.contacts.phoneNumber);
    }

    if (formData.contacts?.email) {
      updateFormData.append("Contacts[Email]", formData.contacts.email);
    }

    if (formData.education?.universityName) {
      updateFormData.append("Education[UniversityName]", formData.education.universityName);
    }

    if (formData.education?.degree) {
      updateFormData.append("Education[Degree]", formData.education.degree);
    }

    formData.languages?.forEach((lang, index) => {
      updateFormData.append(`Languages[${index}][LanguageTitle]`, lang.languageTitle);
      updateFormData.append(`Languages[${index}][Level]`, lang.level);
    });

    formData.links?.forEach((link, index) => {
      updateFormData.append(`Links[${index}][TypeName]`, link.typeName);
      updateFormData.append(`Links[${index}][LinkAddress]`, link.linkAddress);
    });

    formData.skills?.forEach((skill, index) => {
      updateFormData.append(`Skills[${index}][SkillTitle]`, skill.skillTitle);
    });

    if (photo) {
      updateFormData.append("Photo", photo);
    }

    try {
      const response = await fetch(`https://sirius.runasp.net/api/team`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: updateFormData,
      });

      if (response.ok) {
        setUserData(formData); // Update the displayed data with the form data
        setEditMode(false); // Close the modal
      } else {
        console.error("Failed to update data");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  if (loading) {
    return (
      <div style={{ margin: '50px auto', textAlign: 'center', width: '200px', height: '200px' }}>
        <img width={'100%'} src={loadingGif} alt="Loading..." />
      </div>
    );
  }

  if (!userData) {
    return "No data available.";
  }

  return (
    <div>
      <Card sx={{ maxWidth: 600, margin: "auto", padding: 2, mt: 5, position: "relative" }}>
        <CardContent>
          <img
            src={`https://sirius.runasp.net/${userData.photoPath}`}
            style={{
              width: "150px",
              height: "150px",
              border: "2px solid #fff",
              borderRadius: "50%",
            }}
            alt="user"
          />
          <Typography variant="h6" color="initial" sx={{
            background: '#e84545',
            width: '200px',
            margin: 'auto',
            borderRadius: '20px',
            padding: '2px',
          }}> CV is {userData.visible ? <span style={{ color: 'white' }}>visible</span> : <span style={{ color: '#ffe900' }}>hidden</span>}</Typography>
          <Typography variant="h4">{userData.userName}</Typography>
          <Typography variant="subtitle1">{userData.position}</Typography>
          <Typography variant="body1">{userData.summary}</Typography>
          <Typography variant="body1">
            <span style={{ color: "darkmagenta" }}>Phone:</span>{" "}
            {userData.contacts?.phoneNumber}
          </Typography>
          <Typography variant="body1">
            <span style={{ color: "darkmagenta" }}>Email:</span>{" "}
            {userData.contacts?.email}
          </Typography>
          <Typography variant="body1">
            <span style={{ color: "darkmagenta" }}>University:</span>{" "}
            {userData.education?.universityName}
          </Typography>
          <Typography variant="body1">
            <span style={{ color: "darkmagenta" }}>Degree:</span>{" "}
            {userData.education?.degree}
          </Typography>
          <Typography variant="body1">
            <span style={{ color: 'darkmagenta' }}>Languages:</span>
          </Typography>
          <Box sx={{ marginLeft: 2 }}>
            {userData.languages?.map(lang => (
              <Typography key={lang.languageTitle} variant="body1">
                {`${lang.languageTitle} (${lang.level})`}
              </Typography>
            ))}
          </Box>
          <Typography variant="body1">
            <span style={{ color: "darkmagenta" }}>Links:</span>{" "}
          </Typography>
          <Box sx={{ marginLeft: 2 }}>
            {userData.links?.map((link) => (
              <Typography key={link.typeName} variant="body1">
                {`${link.typeName}: ${link.linkAddress}`}
              </Typography>
            ))}
          </Box>
          <Typography variant="body1">
            <span style={{ color: "darkmagenta", display: 'inline-block' }}>Skills:</span>{" "}
          </Typography>
          <Box sx={{ marginLeft: 2 }}>
            {userData.skills?.map((skill, index) => (
              <Typography key={skill.skillTitle} variant="body1" sx={{ display: 'inline' }}>
                {skill.skillTitle}{index < userData.skills.length - 1 && ', '}
              </Typography>
            ))}
          </Box>
          <Button variant="contained" color="primary" onClick={handleEdit}   sx={{
    backgroundColor: '#e84545', // Set your desired background color here
    margin:'15px',
    '&:hover': {
      backgroundColor: '#c73b3b', // Optional: Set background color on hover
    }
  }}>
            Edit
          </Button>
        </CardContent>
      </Card>

      <Modal
        open={editMode}
        onClose={handleClose}
        aria-labelledby="edit-cv-modal"
        aria-describedby="edit-cv-form"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 700,
            overflowY: 'auto',
            maxHeight: '85vh',
            bgcolor: "background.paper",
            borderRadius: "10px",
            boxShadow: 24,
            p: 4,
            display:'flex',alignItems:'center',flexDirection:'column'
          }}
        >
          <Typography id="edit-cv-modal" variant="h6" component="h2" sx={{margin: '10px',
  }}>
            Edit CV
          </Typography>

          <FormControl component="fieldset" sx={{ marginBottom: 2 }}>
            <FormLabel component="legend" sx={{margin:'auto'}}>Profile Visibility</FormLabel>
            <RadioGroup
              row
              value={isVisible}
              onChange={(e) => setIsVisible(e.target.value)}
            >
              <FormControlLabel value="true" control={<Radio />} label="Visible" />
              <FormControlLabel value="false" control={<Radio />} label="Hidden" />
            </RadioGroup>
          </FormControl>

          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              {...register("userName")}
              fullWidth
              label="Name"
              margin="normal"
              variant="outlined"
            />
            <label htmlFor="image"> profile image:  </label>
              <input
              id="image"
              type="file"
              fullWidth
              label="profile image"
              margin="normal"
              onChange={(e) => setPhoto(e.target.files[0])}
            />
            <TextField
              {...register("position")}
              fullWidth
              label="Position"
              margin="normal"
              variant="outlined"
            />
            <TextField
              {...register("summary")}
              fullWidth
              label="Summary"
              multiline
              rows={4}
              margin="normal"
              variant="outlined"
            />
            <TextField
              {...register("contacts.phoneNumber")}
              fullWidth
              label="Phone Number"
              margin="normal"
              variant="outlined"
            />
            <TextField
              {...register("contacts.email")}
              fullWidth
              label="Email"
              margin="normal"
              variant="outlined"
            />
            <TextField
              {...register("education.universityName")}
              fullWidth
              label="University Name"
              margin="normal"
              variant="outlined"
            />
            <TextField
              {...register("education.degree")}
              fullWidth
              label="Degree"
              margin="normal"
              variant="outlined"
            />
            <Typography variant="h6" sx={{margin:'5px auto'}}>Languages</Typography>
            {languageFields.map((field, index) => (
              <div key={field.id} style={{display:'flex',alignItems:'center'}}>
                <TextField
                  {...register(`languages[${index}].languageTitle`)}
                  label="Language"
                  variant="outlined"
                  margin="normal"
                  sx={{ mr: 1 }}
                />
                <TextField
                  {...register(`languages[${index}].level`)}
                  label="Level"
                  variant="outlined"
                  margin="normal"
                />
                <IconButton onClick={() => removeLanguage(index)}>
                  <DeleteIcon />
                </IconButton>
              </div>
            ))}
            <Button
              variant="contained"
              color="primary"sx={{margin:'5px auto',display:'block' ,
                backgroundColor: '#e84545', // Set your desired background color here
                '&:hover': {
                  backgroundColor: '#c73b3b', // Optional: Set background color on hover
                }
              }}
              onClick={() => appendLanguage({ languageTitle: "", level: "" })}
            >
              Add Language
            </Button>

            <Typography variant="h6" sx={{margin:'5px auto'}}>Links</Typography>
            {linkFields.map((field, index) => (
              <div key={field.id} style={{display:'flex',alignItems:'center'}}>
                <TextField
                  {...register(`links[${index}].typeName`)}
                  label="Link Type"
                  variant="outlined"
                  margin="normal"
                  sx={{ mr: 1 }}
                />
                <TextField
                  {...register(`links[${index}].linkAddress`)}
                  label="Link Address"
                  variant="outlined"
                  margin="normal"
                />
                <IconButton onClick={() => removeLink(index)}>
                  <DeleteIcon />
                </IconButton>
              </div>
            ))}
            <Button
              variant="contained"
              color="primary"sx={{margin:'5px auto',display:'block',
                backgroundColor: '#e84545', // Set your desired background color here
                '&:hover': {
                  backgroundColor: '#c73b3b', // Optional: Set background color on hover
                }
              }}
              onClick={() => appendLink({ typeName: "", linkAddress: "" })}
            >
              Add Link
            </Button>

            <Typography variant="h6" sx={{margin:'5px auto'}}>Skills</Typography>
            {skillFields.map((field, index) => (
              <div key={field.id} style={{display:'flex',alignItems:'center'}}>
                <TextField
                  {...register(`skills[${index}].skillTitle`)}
                  label="Skill"
                  variant="outlined"
                  margin="normal"
                />
                <IconButton onClick={() => removeSkill(index)}>
                  <DeleteIcon />
                </IconButton>
              </div>
            ))}
            <Button
              variant="contained"
              color="primary"sx={{margin:'5px auto',display:'block',
                backgroundColor: '#e84545', // Set your desired background color here
                '&:hover': {
                  backgroundColor: '#c73b3b', // Optional: Set background color on hover
                }
              }}
              onClick={() => appendSkill({ skillTitle: "" })}
            >
              Add Skill
            </Button>

          

            <Button type="submit" variant="contained" color="primary"   sx={{
    backgroundColor: '#e84545', // Set your desired background color here
    '&:hover': {
      backgroundColor: '#c73b3b', // Optional: Set background color on hover
    }
  }}>
              Save
            </Button>
          </form>
        </Box>
      </Modal>
    </div>
  );
};

export default MyCV;
